(ns ui.subscription.cancel-modal
  "Modal questionnaire shown when a user cancels their subscription.
   Collects the cancellation reason and optional comments, then dispatches
   the cancel command with the reason data.

   The modal stays open until the server confirms the cancel and shows
   the error when Paddle rejected it: closing right away made a failed
   cancel look like a successful one, and the customer got charged at the
   next renewal (brain-38)."
  (:require [parts.replicant.command :as command]
            [ui.subscription.core :as core]))

(def reasons
  [["too-expensive" "Too expensive"]
   ["missing-features" "Missing features I need"]
   ["switched-competitor" "Switched to a competitor"]
   ["too-difficult" "Too difficult to use"]
   ["no-longer-need" "No longer need the service"]
   ["ig-platform-restrictions" "Instagram Platform Restrictions"]
   ["ig-connection-auth" "Instagram Connection or Authentication Issues"]
   ["pricing-checkout-clarity" "Pricing Model or Checkout Clarity"]
   ["other" "Other"]])

(def cancel-button-class
  ["px-4" "py-2" "text-sm" "font-medium" "rounded-full"
   "text-white" "bg-red-600" "hover:bg-red-700"])

(def failed-fallback
  "Shown when the command failed without a message of its own (an
   exception on the server, a network error)."
  "The cancellation failed. Your subscription is still active. Please try again or contact the support.")

(defn cancel-command
  "The command the modal issues. `parts.replicant.command` logs the
   outcome under the full command map, so the status and error lookups
   below must use exactly this map."
  [{:keys [legacy? selected-reason comment-text]}]
  {:command/kind (if legacy?
                   :command/legacy-subscription-cancel
                   :command/subscription-cancel)
   :command/data {:cancel-reason selected-reason
                  :cancel-comment comment-text}})

(defn on-success
  "The legacy page reloads itself; the wallet page refreshes its queries
   and only then closes the modal."
  [legacy?]
  (if legacy?
    [[:js/location "/ui/legacy-subscription"]]
    [core/import-and-refresh
     [:ui/modal-close]]))

(defn render
  [state]
  (let [selected-reason (::selected-reason state)
        comment-text (::comment-text state)
        legacy? (get-in state [:ui/modal :params :legacy?])
        ;; An on-demand subscription only keeps the payment method on file
        ;; (see storrito.wallet/on-demand?), so cancelling it removes the
        ;; card, nothing else:
        on-demand? (get-in state [:ui/modal :params :on-demand?])
        cmd (cancel-command {:legacy? legacy?
                             :selected-reason selected-reason
                             :comment-text comment-text})
        issued? (command/issued? state cmd)
        error (core/command-error state cmd failed-fallback)]
    [:div.py-4.flex.flex-col.items-center.max-w-md.mx-auto
     [:div.w-16.h-16.rounded-full.bg-amber-100.flex.items-center.justify-center.mb-6
      [:i.fas.fa-exclamation-triangle.text-2xl.text-amber-600]]
     [:h3.text-lg.font-semibold.text-gray-900.text-center.mb-2
      (if on-demand?
        "Remove your payment method?"
        "We're sorry to see you go")]
     [:p.text-sm.text-gray-500.text-center.mb-6
      (if on-demand?
        "Your credits stay and remain usable. We can no longer add credits,
         on request or automatically, until you add a new card. Please let
         us know why, so we can improve."
        "Your subscription ends immediately and your remaining credits stay
         usable. Please let us know why you're cancelling so we can
         improve.")]
     (when legacy?
       [:div.space-y-3.mb-6.w-full.bg-red-50.border.border-red-200.rounded-lg.p-4
        [:div.flex.items-start.gap-3
         [:i.fas.fa-times-circle.text-red-500.mt-0.5]
         [:p.text-sm.text-gray-700
          "This legacy plan is no longer available. Once cancelled, you cannot sign up for it again."]]
        [:div.flex.items-start.gap-3
         [:i.fas.fa-times-circle.text-red-500.mt-0.5]
         [:p.text-sm.text-gray-700
          "You will lose unlimited Instagram seats and story posts."]]
        [:div.flex.items-start.gap-3
         [:i.fas.fa-bolt.text-amber-500.mt-0.5]
         [:p.text-sm.text-gray-700.font-semibold
          "Cancellation takes effect immediately. All services will end right away."]]])
     [:div.space-y-3.mb-6.w-full
      (map
        (fn [[value label]]
          [:label.flex.items-center.gap-3.cursor-pointer.p-2.rounded.hover:bg-gray-50
           {:key value}
           [:input {:type "radio"
                    :name "cancel-reason"
                    :class ["accent-red-600"]
                    :checked (= selected-reason value)
                    :on {:click [[:store/assoc-in [::selected-reason] value]]}}]
           [:span.text-sm.text-gray-700 label]])
        reasons)]
     [:div.mb-6.w-full
      [:label.block.text-sm.font-medium.text-gray-700.mb-1
       "Additional comments (optional)"]
      [:textarea.w-full.border.border-gray-300.rounded-md.p-2.text-sm.text-gray-700.focus:ring-1.focus:ring-red-500.focus:border-red-500
       {:rows 3
        :maxlength 500
        :placeholder "Tell us more..."
        :value (or comment-text "")
        :on {:input [[:store/assoc-in [::comment-text] :event/target.value]]}}]]
     (when error
       [:p.mb-4.w-full.rounded-md.bg-red-50.p-3.text-sm.text-red-700
        error])
     [:div.flex.gap-3.justify-end.w-full
      [:button {:class ["px-4" "py-2" "text-sm" "font-medium" "rounded-full"
                        "border" "border-gray-300" "text-gray-700"
                        "bg-white" "hover:bg-gray-50"]
                :on {:click [[:ui/modal-close]]}}
       (if on-demand?
         "Keep it"
         "Keep my subscription")]
      [:button (cond-> {:class cancel-button-class
                        :on {:click [[:data/command
                                      cmd
                                      {:on-success (on-success legacy?)}]]}}
                 (or (not selected-reason)
                     issued?)
                 (assoc :disabled true))
       (if on-demand?
         "Remove payment method"
         "Cancel subscription")]]]))

(def modal
  {:modal-id :modals/subscription-cancel-modal
   :on-load (fn [_location]
              [[:store/assoc-in [::selected-reason] nil]
               [:store/assoc-in [::comment-text] nil]])
   :render #'render})
